import React, { useState, useEffect, useRef } from 'react';
import {renderPayPalButton } from './paypalSDKLoader'; // Import the loader
import axios from 'axios';
import './App.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Accordion } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS

const DEMO_RESULTS = [
    {
        title: 'Grade 8 Piano Demo',
        href: '?result=748e8557-4725-4b38-af31-65e7a6896c1f',
        summary: 'Long-form sample analysis with examiner feedback, score breakdown, and certificate download.',
    },
    {
        title: 'Grade 3 Piano Demo',
        href: '?result=abf3d52d-066e-42f2-b1f5-22b83f6156f8',
        summary: 'A lighter example that shows the kind of feedback younger candidates and parents will see.',
    },
];

const VALUE_POINTS = [
    'ABRSM-oriented performance feedback',
    'Structured piece-by-piece score breakdowns',
    'Instant downloadable PDF certificate',
];

const HOW_IT_WORKS = [
    {
        title: 'Upload or Record',
        body: 'Use a single performance-grade recording with spoken introduction, or type the candidate and piece details yourself.',
    },
    {
        title: 'Get Examiner-Style Notes',
        body: 'LudwigX returns a concise summary, detailed technical observations, and structured examiner feedback.',
    },
    {
        title: 'Share the Report',
        body: 'Open the result link later, review the tabs with a teacher or parent, and download the PDF certificate.',
    },
];

const AUDIENCE_SEGMENTS = [
    {
        title: 'Students',
        body: 'Practice more intentionally between lessons with concrete notes on rhythm, accuracy, dynamics, and musicality.',
    },
    {
        title: 'Parents',
        body: 'See what a performance recording contains before an exam submission and spot obvious gaps early.',
    },
    {
        title: 'Teachers',
        body: 'Use LudwigX as a fast second-pass review tool for mock submissions, homework, and recital preparation.',
    },
];

function App() {
    const [audioFile, setAudioFile] = useState(null);
    const [analysisResults, setAnalysisResults] = useState(null); // Store all results
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('summary'); // Track the active tab
    const [canDownloadCertificate, setCanDownloadCertificate] = useState(false);
    const paypalRef = useRef(null); //Ref to make sure the effect only run once
    const [numPieces, setNumPieces] = useState(4); // default to 4
    const [hasSpokenIntro, setHasSpokenIntro] = useState(true); // default to checked
    const [candidateName, setCandidateName] = useState(""); // State for the candidate's name
    const [pieceTitles, setPieceTitles] = useState(Array(numPieces).fill("")); // State for the piece titles. Must be called after numpieces.
    const [isRecording, setIsRecording] = useState(false); // Recording state
    const [recordingTime, setRecordingTime] = useState(0); // Duration in seconds

    const [hasRecorded, setHasRecorded] = useState(false); // Hide file input after recording

    const mediaRecorderRef = useRef(null); // MediaRecorder instance
    const recordingIntervalRef = useRef(null); // Interval for timer

    const scrollToForm = () => {
        document.getElementById('analysis-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Function to handle changes to num Pieces
    const handleNumPiecesChange = (event) => {
        const newNumPieces = parseInt(event.target.value);
        setNumPieces(newNumPieces);
        setPieceTitles(Array(newNumPieces).fill("")); // Reset the piece titles array
    };

    const handlePieceTitleChange = (index, event) => {
        const newPieceTitles = [...pieceTitles];
        newPieceTitles[index] = event.target.value;
        setPieceTitles(newPieceTitles);
    };

    // Function to extract query parameters from the URL
    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    };


    // Load data from backend if UUID parameter exists, otherwise do nothing
    useEffect(() => {
        const resultId = getQueryParam('result'); //Check for the "result" parameter

        if (resultId) {
            //Construct the getresult URL
            const apiBaseUrl = window.location.origin;
            let apiUrl;
            if (window.location.port) {
                apiUrl = `${apiBaseUrl.split(':')[0]}:${apiBaseUrl.split(':')[1]}:5000/get_result/${resultId}`; //Custom Solution For Ports
            } else {
                apiUrl = `${apiBaseUrl}/get_result/${resultId}`; // If the react app is on a common port
            }
            //fetch results from JSON and then to the page
            fetchResults(apiUrl);
        }
    }, []);

    const handleFileChange = (event) => {
        setAudioFile(event.target.files[0]);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            let chunks = [];
            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };
            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                setAudioFile(blob);

                setHasRecorded(true);

                chunks = [];
            };
            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start();
            setIsRecording(true);

            setHasRecorded(true);

            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime((t) => t + 1);
            }, 1000);
        } catch (err) {
            console.error('Error accessing microphone', err);
            setError('Microphone access denied.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
        }
        setIsRecording(false);
        clearInterval(recordingIntervalRef.current);
        setRecordingTime(0);
    };

    const fetchResults = async (apiUrl) => {
        try {
            const response = await axios.get(apiUrl); // Get endpoint

            setAnalysisResults(response.data);
            setCanDownloadCertificate(true);
        } catch (error) {
            console.error("There was an error fetching JSON results.", error);
            setError("Something bad happened");
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!audioFile) {
            setError('Please select an audio file.');
            return;
        }

        setLoading(true);
        setError('');

        const formData = new FormData();
        formData.append('audio', audioFile);
        formData.append('candidate_name', candidateName);
        formData.append('num_pieces', numPieces);
        formData.append('has_spoken_intro', hasSpokenIntro);
        for (let i = 0; i < numPieces; i++) {
            formData.append(`piece_title_${i}`, pieceTitles[i]);
        }

        try {
            const apiBaseUrl = window.location.origin;
            let apiUrl;

            if (window.location.port) {
                apiUrl = `${apiBaseUrl.split(':')[0]}:${apiBaseUrl.split(':')[1]}:5000/analyze_audio`; //Custom Solution For Ports
            } else {
                apiUrl = `${apiBaseUrl}/analyze_audio`; // If the react app is on a common port
            }
           
            const response = await axios.post(
                apiUrl,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setAnalysisResults(response.data);

            setCanDownloadCertificate(true);

        } catch (err) {
            console.error(err);
            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error); // Set the backend error
            } else {
                setError('Error analyzing audio. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCertificate = async () => {
        try {
            const apiBaseUrl = window.location.origin;
            let apiUrl;

        // Check if a port is already specified in window.location.origin
        if (window.location.port) {
            apiUrl = `${apiBaseUrl.split(':')[0]}:${apiBaseUrl.split(':')[1]}:5000/certificate`; //Custom Solution For Ports
        } else {
            apiUrl = `${apiBaseUrl}/certificate`; // If the react app is on a common port
        }

            const response = await axios.post(
                apiUrl,
                analysisResults, // Send the analysis results to the backend
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/pdf',  // Expect a PDF response
                    },
                    responseType: 'blob',  // Important: Tell axios to expect a binary response
                }
            );

            // Create a blob from the PDF stream
            const blob = new Blob([response.data], { type: 'application/pdf' });

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a link and trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificate.pdf'); // or any other extension
            document.body.appendChild(link);
            link.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error downloading certificate:', error);
            setError('Error downloading certificate. Please try again.');
        }
    };

    const formatAbrsmFeedback = (feedback) => {
        if (!feedback) return "No feedback available.";
        if (typeof feedback === "string") return feedback;
        if (typeof feedback !== "object") return String(feedback);

        const pieces = Array.isArray(feedback.piece_scores) ? feedback.piece_scores : [];
        const overall = feedback.overall_performance || {};
        const totalScore = feedback.total_score ?? "N/A";
        const finalGrade = feedback.final_grade ?? "N/A";

        let md = "";

        if (pieces.length) {
            md += "### Piece Scores\n";
            pieces.forEach((piece) => {
                const pieceNum = piece?.piece ?? "N/A";
                const score = piece?.score ?? "N/A";
                const pieceFeedback = piece?.feedback ?? "No feedback provided.";
                md += `**Piece ${pieceNum}:** ${score}/30\n\n${pieceFeedback}\n\n`;
            });
        }

        if (overall.score != null || overall.feedback) {
            const overallScore = overall.score ?? "N/A";
            const overallFeedback = overall.feedback ?? "No feedback provided.";
            md += "### Overall Performance\n";
            md += `**Score:** ${overallScore}/30\n\n${overallFeedback}\n\n`;
        }

        if (feedback.total_score != null || feedback.final_grade) {
            md += "### Final Result\n";
            md += `**Total Score:** ${totalScore}/150\n\n**Final Grade:** ${finalGrade}\n`;
        }

        return md.trim() || "No feedback available.";
    };

    const renderTabContent = () => {
        if (!analysisResults) return null;

        switch (activeTab) {
            case 'summary':
                return (
                    <div className="tab-content">
                        <h2>Summary:</h2>
                        <h3>Candidate: {analysisResults.candidate_name}</h3> {/* Display candidate name */}
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {analysisResults.summary}
                        </ReactMarkdown>
                    </div>
                );
            case 'detailed':
                return (
                    <div className="tab-content">
                        <h2>Detailed Analysis:</h2>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {analysisResults.detailed_analysis}
                        </ReactMarkdown>
                    </div>
                );
            case 'abrsm':
                return (
                    <div className="tab-content">
                        <h2>Examiner Feedback:</h2>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {formatAbrsmFeedback(analysisResults.abrsm_feedback)}
                        </ReactMarkdown>
                    </div>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        if (!paypalRef.current) {
            renderPayPalButton(); // Call the centralized render function
            paypalRef.current = true;
        }
    }, []);


    return (
        <div className="App">
        <section className="hero">
            <div className="hero-copy">
                <p className="eyebrow">ABRSM Piano Performance Feedback</p>
                <h1>LudwigX</h1>
                <h2>Upload a performance recording and get examiner-style feedback in minutes.</h2>
                <p className="hero-text">
                    Built for piano performance-grade practice, LudwigX turns one recording into a summary,
                    detailed technical notes, examiner feedback, score breakdowns, and a downloadable PDF report.
                </p>
                <div className="hero-actions">
                    <button type="button" onClick={scrollToForm}>Upload Your Recording</button>
                    <a className="secondary-link" href={DEMO_RESULTS[0].href}>View Demo Report</a>
                </div>
            </div>
            <div className="hero-panel">
                <div className="brand-lockup brand-lockup-panel">
                    <div className="brand-logo-shell">
                        <img className="brand-logo brand-logo-panel" src="/logo-mark.png" alt="LudwigX crest logo" />
                    </div>
                </div>
                <h3>What LudwigX gives you</h3>
                <ul className="value-list">
                    {VALUE_POINTS.map((point) => (
                        <li key={point}>{point}</li>
                    ))}
                </ul>
                <p className="hero-note">
                    Best fit today: ABRSM-style piano performance submissions with four pieces and a spoken introduction.
                </p>
            </div>
        </section>

        <section className="proof-grid">
            {HOW_IT_WORKS.map((item) => (
                <article key={item.title} className="proof-card">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                </article>
            ))}
        </section>

        <section className="demo-section">
            <div className="section-heading">
                <p className="eyebrow">Try Before You Upload</p>
                <h2>Open a full sample report</h2>
                <p>
                    Public result pages are the fastest way to understand the LudwigX output before you commit a full recording.
                </p>
            </div>
            <div className="demo-grid">
                {DEMO_RESULTS.map((demo) => (
                    <article key={demo.title} className="demo-card">
                        <h3>{demo.title}</h3>
                        <p>{demo.summary}</p>
                        <a href={demo.href}>Open sample result</a>
                    </article>
                ))}
            </div>
        </section>

        <section className="audience-section">
            <div className="section-heading">
                <p className="eyebrow">Who It Helps</p>
                <h2>Useful between lessons, before submissions, and after mock exams.</h2>
            </div>
            <div className="audience-grid">
                {AUDIENCE_SEGMENTS.map((item) => (
                    <article key={item.title} className="audience-card">
                        <h3>{item.title}</h3>
                        <p>{item.body}</p>
                    </article>
                ))}
            </div>
        </section>

        <div className="intro">
            <p className="eyebrow">Upload Setup</p>
            <h2>Prepare one clean recording</h2>
            <p>
                For best results, upload or record a complete performance-grade submission with a spoken introduction,
                then the pieces played consecutively. If the introduction is missing, you can type the candidate and piece details below.
            </p>
        </div>

        <form id="analysis-form" onSubmit={handleSubmit}>



            <div className="form-inline">
                    <label htmlFor="numPieces">Number of Pieces:</label>
                    <input
                        type="number"
                        id="numPieces"
                        name="numPieces"
                        value={numPieces}
                        onChange={handleNumPiecesChange}
                        min="1"
                        max="10"
                    />
                </div>

                <div className="form-inline">
                    <label htmlFor="hasSpokenIntro">
                        Has a spoken intro, including the candidate name and the title and composer of each piece:
                        <span
                            className="tooltip"
                            title="Candidate introduces themselves with their name, syllabus year (if applicable), and the names of each exam piece."
                        >
                            ?
                        </span>
                    </label>
                    <input
                        type="checkbox"
                        id="hasSpokenIntro"
                        name="hasSpokenIntro"
                        checked={hasSpokenIntro}
                        onChange={(e) => setHasSpokenIntro(e.target.checked)}
                    />
                </div>


                {!hasSpokenIntro && (
                    <div className="conditional-input-panel">
                        <label htmlFor="candidateName">Candidate Name:</label>
                        <input
                            type="text"
                            id="candidateName"
                            name="candidateName"
                            value={candidateName}
                            onChange={(e) => setCandidateName(e.target.value)}
                        />
                         {Array.from({ length: numPieces }, (_, index) => (
                            <div key={index}>
                                <label htmlFor={`pieceTitle${index}`}>  Title, Composer: {index + 1}</label>
                                <input
                                    type="text"
                                    id={`pieceTitle${index}`}
                                    name={`pieceTitle${index}`}
                                    value={pieceTitles[index] || ""} // Ensure value is not undefined
                                    onChange={(e) => handlePieceTitleChange(index, e)}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className="record-controls">
                    <button type="button" onClick={startRecording} disabled={isRecording}>Record</button>
                    <button type="button" onClick={stopRecording} disabled={!isRecording}>Stop</button>
                    {isRecording && <span className="recording-status">Recording... {recordingTime}s</span>}
                </div>

                {!hasRecorded && (
                    <input type="file" accept="audio/*" onChange={handleFileChange} />
                )}



                <button type="submit" disabled={loading}>
                    {loading ? 'Analyzing...' : 'Analyze Audio'}
                </button>
                {error && <div className="error">{error}</div>}
        </form>

        {analysisResults && (
            <div className="tabs">
                <button onClick={() => setActiveTab('summary')} className={activeTab === 'summary' ? 'active' : ''}>
                    Summary
                </button>
                <button onClick={() => setActiveTab('detailed')} className={activeTab === 'detailed' ? 'active' : ''}>
                    Detailed Analysis
                </button>
                <button onClick={() => setActiveTab('abrsm')} className={activeTab === 'abrsm' ? 'active' : ''}>
                    Examiner Feedback
                </button>
            </div>
        )}

        {renderTabContent()}

                             {/* Buttons: Download Certificate & Donate */}
        <div className="button-container">
             {canDownloadCertificate && (
                 <button onClick={handleDownloadCertificate}>Download Certificate</button>
             )}

             {/* PayPal Donate Button and Beta Text */}
            <div className="donate-section">
                  <p>If this is useful to you, and you would like us to continue development of LudwigX, please</p>
                     <div id="donate-button"></div>
           </div>
        </div>

        {/* React Bootstrap Accordion */}
        <div className="faq-section">
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Frequently Asked Questions</Accordion.Header>
              <Accordion.Body>
                <ul>
                  <li>
                    <strong>What audio formats are supported?</strong>
                    <p>LudwigX supports M4A, MP3, AAC, OGG, FLAC, and WebM. Maximum 50MB. Audio-only files work best.</p>
                  </li>
                  <li>
                    <strong>What should the audio contain?</strong>
                    <p>
                      A spoken intro with the candidate's name, syllabus year, and the names of the pieces, followed by the full performance.
                      See the{" "}
                      <a
                        href="https://www.abrsm.org/en-gb/performance-grades/guidance"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ABRSM Performance Exam guidance
                      </a>{" "}
                      for the official submission requirements.
                    </p>
                  </li>
                  <li>
                    <strong>How accurate is the analysis?</strong>
                    <p>
                      LudwigX is designed as a practice and review tool. It is useful for surfacing issues and patterns,
                      but it should supplement, not replace, a qualified teacher.
                    </p>
                  </li>
                  <li>
                    <strong>Is LudwigX only for piano?</strong>
                    <p>
                      The clearest fit today is ABRSM-style piano performance work. Other instruments may still return results,
                      but the quality will vary.
                    </p>
                  </li>
                </ul>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <a href="mailto:feedback@ludwigx.com"> FEEDBACK! </a>
         <a href="/privacy.html"> | Privacy Policy</a>
    </div>
    );
}

export default App;
